import { createAuth0Client, type Auth0Client, type User } from "@auth0/auth0-spa-js";

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN || "";
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID || "";
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || "";

export interface AuthManager {
  init(): Promise<void>;
  login(): Promise<User | null>;
  logout(): Promise<void>;
  isAuthenticated(): boolean;
  /**
   * Access token for the scores API, or null when not signed in (EB-617).
   * The API derives the player's identity from this token's `sub` and ignores anything the client
   * puts in the request body, so this is the only thing that establishes who is submitting.
   */
  getAccessToken(): Promise<string | null>;
  getUserName(): string | null;
  getUserEmail(): string | null;
  getUserPicture(): string | null;
  getUser(): User | null;
  wasRedirectLogin(): boolean;
}

let instance: AuthManager | null = null;

export function getAuthManager(): AuthManager {
  if (instance) return instance;

  let client: Auth0Client | null = null;
  let user: User | null = null;
  let redirectLogin = false;

  instance = {
    async init() {
      if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID) return;

      try {
        client = await createAuth0Client({
          domain: AUTH0_DOMAIN,
          clientId: AUTH0_CLIENT_ID,
          cacheLocation: "localstorage",  // survive page reloads (game restart)
          authorizationParams: {
            audience: AUTH0_AUDIENCE || undefined,
          },
        });

        // Handle redirect callback (mobile login flow)
        const query = window.location.search;
        const wasRedirect = query.includes("code=") && query.includes("state=");
        if (wasRedirect) {
          await client.handleRedirectCallback();
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        if (await client.isAuthenticated()) {
          user = (await client.getUser()) ?? null;
          if (wasRedirect) redirectLogin = true;
        }
      } catch (err) {
        console.error("Auth0 init failed:", err);
        client = null;
      }
    },

    async login(): Promise<User | null> {
      if (!client) return null;

      try {
        // Always use redirect — popup is blocked by most browsers by default.
        // Auth0 will redirect back here with ?code=&state= params that init() picks up.
        // Use origin+pathname (not href) so query strings don't break the callback URL match.
        await client.loginWithRedirect({
          authorizationParams: {
            redirect_uri: window.location.origin + window.location.pathname,
          },
        });
        return null; // page navigates away; nothing runs after this
      } catch (err) {
        console.error("Auth0 login failed:", err);
        return null;
      }
    },

    async logout(): Promise<void> {
      if (!client) return;

      try {
        await client.logout({ openUrl: false });
        user = null;
      } catch (err) {
        console.error("Auth0 logout failed:", err);
      }
    },

    isAuthenticated(): boolean {
      return !!user;
    },

    async getAccessToken(): Promise<string | null> {
      if (!client) return null;
      try {
        // Needs the audience configured at createAuth0Client above, otherwise Auth0 issues an
        // opaque token instead of a JWT and the API Gateway authorizer rejects it.
        return await client.getTokenSilently();
      } catch (err) {
        // Expired session with no refresh token, or third-party cookies blocked. The caller
        // treats null as "not signed in" rather than retrying.
        console.error("Auth0 getTokenSilently failed:", err);
        return null;
      }
    },

    getUserName(): string | null {
      // Deliberately does NOT fall back to the email address (EB-617). This value is sent as
      // `playerName` and rendered on a public leaderboard; falling back to email is how three
      // real addresses ended up displayed on screen. No name means no name — the API substitutes
      // a neutral label.
      return user?.name || user?.nickname || null;
    },

    getUserEmail(): string | null {
      return user?.email || null;
    },

    getUserPicture(): string | null {
      return user?.picture || null;
    },

    getUser(): User | null {
      return user;
    },

    wasRedirectLogin(): boolean {
      if (redirectLogin) {
        redirectLogin = false;
        return true;
      }
      return false;
    },
  };

  return instance;
}
